import { Currency, Voucher } from '@accedo/one-plugin-sdk';
import { FC } from 'react';
import { CSSTransition } from 'react-transition-group';

import { FormInput } from '#/components/Form/FormInput/FormInput';
import { translateDiscount } from '#/plugins/paywall/utils';
import { ButtonVariant } from '#/service/constants';
import { noop } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { useDiscount } from './Discount.hook';
import {
  VoucherApplied,
  VoucherForm,
  VoucherFormSubmitButton,
  VoucherInformation,
  VoucherInformationIcon,
  VoucherInformationText,
  VoucherRemoveButton,
  VoucherRemoveIcon,
  VoucherText,
} from './Discount.styled';

interface Props {
  isDisabled?: boolean;
  onPriceChange: (price: { amount: string; currency?: Currency }) => void;
  onVoucherChange: (planId: string, voucherCode: string) => Promise<Voucher>;
  onVoucherRemove: () => Promise<void>;
  hasOwnUi: boolean;
  id: string;
  price: { amount: string; currency?: Currency };
}

export const Discount: FC<Props> = ({
  id,
  price,
  isDisabled,
  onPriceChange,
  onVoucherChange,
  onVoucherRemove,
  hasOwnUi,
}) => {
  const {
    inputState,
    voucher,
    voucherError,
    isLoading,
    formRef,
    updateInputState,
    submitVoucherCode,
    removeVoucher,
    key,
    label,
    variant,
    isSubmitButtonDisabled,
    isFormInputDisabled,
  } = useDiscount({
    id,
    price,
    onPriceChange,
    onVoucherChange,
    onVoucherRemove,
    hasOwnUi,
    isDisabled,
  });

  const voucherInfoMessage = tb('iap.voucher.info.message');

  return (
    <>
      {!voucher && (
        <VoucherForm onSubmit={submitVoucherCode} ref={formRef}>
          <FormInput
            data-testid="voucher-form-input"
            dataKey={key}
            disabled={isFormInputDisabled}
            errorMessages={voucherError ? [voucherError] : undefined}
            label={label}
            state={inputState}
            updateErrorMessages={noop}
            updateState={updateInputState}
            variant={variant}
          />

          <VoucherFormSubmitButton
            data-testid="voucher-form-submit"
            disabled={isSubmitButtonDisabled}
            isLoading={isLoading}
            type="submit"
          >
            {tb('button.apply')}
          </VoucherFormSubmitButton>
        </VoucherForm>
      )}
      <CSSTransition in={!!voucher} timeout={200}>
        <VoucherApplied>
          <VoucherText>
            {voucher
              ? translateDiscount(price.currency ?? Currency.UNKNOWN, voucher)
              : null}
          </VoucherText>
          {voucher && (
            <VoucherRemoveButton
              aria-label={tb('button.remove')}
              data-testid="voucher-remove-button"
              isLoading={isLoading}
              onClick={removeVoucher}
              variant={ButtonVariant.Ghost}
            >
              <VoucherRemoveIcon name="cross" />
            </VoucherRemoveButton>
          )}
        </VoucherApplied>
      </CSSTransition>
      {voucherInfoMessage ? (
        <VoucherInformation data-testid="voucher-information">
          <VoucherInformationIcon name="info_circle" />
          <VoucherInformationText>{voucherInfoMessage}</VoucherInformationText>
        </VoucherInformation>
      ) : null}
    </>
  );
};
